// Client-scoped cache layer for the Workspace (Dashboard.jsx).
//
// Why this exists:
//   The Dashboard.jsx client-switching surface caches per-client data
//   (uploaded photo, recently-generated looks) in sessionStorage so the
//   workspace re-mounts and client switches feel instant — no spinner,
//   no flash, no "blank" frame between selections. Historically this
//   logic lived inline in 4+ different places in Dashboard.jsx, and each
//   site had its own subtly-different read/write shape — which is how
//   cross-client photo bleed regressions kept popping up.
//
// Invariants enforced here:
//   1. Every cache key is scoped by client id. Master/admin "_demo"
//      fallbacks live under a sentinel key, never leak across clients.
//   2. Reads are stale-guarded by client id at the call site — the
//      caller MUST hand in the client id and we never resolve "the
//      currently selected client" from inside this module.
//   3. Writes silently swallow Quota errors (sessionStorage is best-
//      effort, not a source of truth — the gallery API is).
//
// Storage layout:
//   sessionStorage.client_photo_<id> = { b64, preview, ts }
//   localStorage.client_photo_<id>   = { b64, preview, ts }  (durable copy)
//   sessionStorage.client_looks_<id> = [stripped look records]
//
// NOT covered (yet — incremental refactor):
//   • Fabric swatches
//   • Wedding party photo
//   • Color analysis state
//   These remain inline in Dashboard.jsx for now and will move in the
//   Phase 2 useClientSwitch hook extraction.

const SESSION = typeof window !== 'undefined' ? window.sessionStorage : null;
const LOCAL = typeof window !== 'undefined' ? window.localStorage : null;

const photoKey = (clientId) => `client_photo_${clientId || 'demo'}`;
const looksKey = (clientId) => `client_looks_${clientId || 'demo'}`;
const photoIndexKey = 'client_photo_index'; // LRU list of cached client ids

// Soft cap on durable per-client photo caches — we keep the 3 most-recent
// clients' photos in localStorage so device-switch + cross-tab still feels
// instant, but we don't unbounded-grow the storage budget.
const PHOTO_LRU_LIMIT = 3;

/** Read the cached photo for one specific client.
 *  Tries sessionStorage first (latest), falls back to localStorage. Returns
 *  null when nothing is cached or the entry is malformed.
 */
export function readClientPhoto(clientId) {
  if (!clientId) return null;
  try {
    const fromSession = SESSION?.getItem(photoKey(clientId));
    if (fromSession) {
      const parsed = JSON.parse(fromSession);
      if (parsed?.b64) return parsed;
    }
  } catch { /* noop */ }
  try {
    const fromLocal = LOCAL?.getItem(photoKey(clientId));
    if (fromLocal) {
      const parsed = JSON.parse(fromLocal);
      if (parsed?.b64) return parsed;
    }
  } catch { /* noop */ }
  return null;
}

/** Persist a client photo to BOTH sessionStorage (fast warm path) and
 *  localStorage (durable across reloads). Also maintains an LRU index in
 *  localStorage so we can prune old entries when the limit is hit.
 */
export function writeClientPhoto(clientId, { b64, preview }) {
  if (!clientId || !b64) return;
  const payload = JSON.stringify({ b64, preview, ts: Date.now() });
  try { SESSION?.setItem(photoKey(clientId), payload); } catch { /* quota */ }
  try {
    LOCAL?.setItem(photoKey(clientId), payload);
    const idx = JSON.parse(LOCAL?.getItem(photoIndexKey) || '[]');
    const next = [clientId, ...idx.filter((x) => x !== clientId)].slice(0, PHOTO_LRU_LIMIT);
    LOCAL?.setItem(photoIndexKey, JSON.stringify(next));
    // Evict anything that fell off the LRU
    idx.filter((x) => !next.includes(x)).forEach((stale) => {
      try { LOCAL?.removeItem(photoKey(stale)); } catch { /* noop */ }
    });
  } catch { /* quota */ }
}

/** Read cached generated looks for one specific client. Returns [] when
 *  empty or malformed. Strips any look without an image so the caller never
 *  paints a broken card.
 */
export function readClientLooks(clientId) {
  if (!clientId) return [];
  try {
    const raw = SESSION?.getItem(looksKey(clientId));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((l) => l && (l.image || l.id));
  } catch {
    return [];
  }
}

/** Persist a list of generated looks for one client. Caller is responsible
 *  for stripping any non-serializable fields (e.g. File objects, refs).
 */
export function writeClientLooks(clientId, looks) {
  if (!clientId || !Array.isArray(looks)) return;
  try {
    SESSION?.setItem(looksKey(clientId), JSON.stringify(looks));
  } catch { /* quota */ }
}

/** Wipe the cached selection markers (NOT the per-client photo/looks
 *  caches — those stay so a quick re-login is still instant). Used by the
 *  gate's "Return to login" escape hatch and full sign-outs.
 */
export function clearSelectionMarkers() {
  try { LOCAL?.removeItem('selected_client_id'); } catch { /* noop */ }
  try { LOCAL?.removeItem('selected_client_name'); } catch { /* noop */ }
}
